import { useMemo } from 'react';
import { Mold } from '../types/mold';
import { StatusFilterType } from '../components/MoldList/types';
import { getStatusMatcher } from './useStatusCounts';

export interface SearchConfig {
  query: string;
  field: string;
}

export const useFilteredMolds = (
  molds: Mold[],
  searchConfig: SearchConfig,
  statusFilter: StatusFilterType | StatusFilterType[],
  customerFilter: string,
  attentionThreshold: number = 1,
) => {
  return useMemo(() => {
    let filtered = [...molds];

    // Apply status filter - handle both single status and array of statuses
    const statusFilters = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
    
    if (!statusFilters.includes('ALL') && statusFilters.length > 0) {
      filtered = filtered.filter((mold) => {
        // Check if mold matches any of the selected statuses using centralized matchers
        return statusFilters.some((status) => {
          const matcher = getStatusMatcher(status);
          return matcher(mold);
        });
      });
    }

    // Apply customer filter
    if (customerFilter !== 'ALL') {
      filtered = filtered.filter((mold) =>
        mold.customer.code === customerFilter || mold.customer.name === customerFilter
      );
    }

    // Apply search filter based on selected field
    if (searchConfig.query && searchConfig.query.trim()) {
      const searchLower = searchConfig.query.toLowerCase();

      filtered = filtered.filter((mold) => {
        switch (searchConfig.field) {
          case 'moldCode':
            return String(mold.moldCode ?? '').toLowerCase().includes(searchLower);
          case 'moldName':
            return String(mold.moldName ?? '').toLowerCase().includes(searchLower);
          case 'moldId':
            return (
              mold.moldNumber.toLowerCase().includes(searchLower) ||
              mold.sbiId.toLowerCase().includes(searchLower)
            );
          case 'partNo':
            return mold.productParts.some((part) =>
              part.partNumber.toLowerCase().includes(searchLower),
            );
          case 'partName':
            return mold.productParts.some((part) =>
              part.partName.toLowerCase().includes(searchLower),
            );
          case 'customer':
            return (
              mold.customer.code.toLowerCase().includes(searchLower) ||
              mold.customer.name.toLowerCase().includes(searchLower)
            );
          case 'all':
          default:
            return (
              mold.moldNumber.toLowerCase().includes(searchLower) ||
              String(mold.moldName ?? '').toLowerCase().includes(searchLower) ||
              String(mold.moldCode ?? '').toLowerCase().includes(searchLower) ||
              mold.sbiId.toLowerCase().includes(searchLower) ||
              mold.customer.code.toLowerCase().includes(searchLower) ||
              mold.customer.name.toLowerCase().includes(searchLower) ||
              mold.productParts.some(
                (part) =>
                  part.partNumber.toLowerCase().includes(searchLower) ||
                  part.partName.toLowerCase().includes(searchLower),
              )
            );
        }
      });
    }

    return filtered;
  }, [
    molds,
    searchConfig.query,
    searchConfig.field,
    statusFilter,
    customerFilter,
    attentionThreshold,
  ]);
};